import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import { format, parseISO, isToday, isTomorrow } from 'date-fns';
import { ru } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Sidebar } from '@/components/Sidebar';
import { BookingLinksDialog } from '@/components/BookingLinksDialog';
import { DayCycleGreeting } from '@/components/DayCycleGreeting';
import { CalendarDays, Clock, Users, CalendarCheck, AlertCircle, ArrowRight, XCircle, Check, Link2, FileUp, Settings, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

const statusConfig = {
  pending_admin: { label: 'Ожидает админа', className: 'bg-orange-900/30 text-orange-400 border border-orange-900/50' },
  pending_doctor: { label: 'Ожидает врача', className: 'bg-sky-900/30 text-sky-400 border border-sky-900/50' },
  pending: { label: 'Ожидает', className: 'bg-amber-900/30 text-amber-400 border border-amber-900/50' },
  confirmed: { label: 'Подтверждена', className: 'bg-green-900/30 text-green-400 border border-green-900/50' },
  cancelled: { label: 'Отменена', className: 'bg-red-900/30 text-red-400 border border-red-900/50' },
};

export default function DashboardPage() {
  const [appointments, setAppointments] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState('doctor');
  const [confirmCancel, setConfirmCancel] = useState(null);
  const [linksOpen, setLinksOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!document.cookie.includes('pp_csrf')) {
      navigate('/login');
      return;
    }
    api.get('/auth/me').then(({ data }) => setUserRole(data.role || 'doctor')).catch(() => {});
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [apptRes, settingsRes] = await Promise.all([
        api.get(`/appointments`),
        api.get(`/settings`),
      ]);
      setAppointments(Array.isArray(apptRes.data) ? apptRes.data : []);
      setSettings(settingsRes.data);
    } catch (err) {
      if (err.response?.status === 401) navigate('/login');
    }
    setLoading(false);
  };

  const confirmAppt = async (id) => {
    try {
      const { data } = await api.put(`/appointments/${id}/confirm`, {});
      if (data.status === 'pending_doctor') {
        toast.success('Заявка передана специалисту');
      } else {
        toast.success('Запись подтверждена');
      }
      fetchData();
    } catch { toast.error('Ошибка подтверждения'); }
  };

  const cancelAppt = async (id) => {
    try {
      await api.put(`/appointments/${id}/cancel`, {});
      toast.success('Запись отменена');
      fetchData();
    } catch { toast.error('Ошибка отмены'); }
  };

  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const todayAppointments = appointments
    .filter((a) => a.date === todayStr && a.status !== 'cancelled')
    .sort((a, b) => a.time.localeCompare(b.time));
  const pendingAppointments = appointments.filter((a) => ['pending_admin', 'pending_doctor', 'pending'].includes(a.status));
  const confirmedTotal = appointments.filter((a) => a.status === 'confirmed').length;
  const thisWeekConfirmed = appointments.filter((a) => {
    const d = parseISO(a.date);
    const now = new Date();
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - now.getDay() + 1);
    return a.status === 'confirmed' && d >= weekStart;
  }).length;

  const isOwner = userRole === 'owner';
  const isEmpty = appointments.length === 0;

  if (loading) {
    return (
      <div className="flex min-h-screen" style={{ background: 'var(--app-bg)' }}>
        <Sidebar />
        <main className="flex-1 p-4 lg:p-8 pt-16 lg:pt-8 overflow-auto">
          <div className="max-w-6xl mx-auto">
            <div className="mb-10 flex flex-col items-center gap-3">
              <Skeleton className="h-9 w-64" />
              <Skeleton className="h-4 w-40" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Skeleton className="h-64 rounded-xl" />
              <Skeleton className="h-64 rounded-xl" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  const stats = [
    { key: 'today', icon: CalendarDays, label: 'Сегодня', value: todayAppointments.length, color: 'text-amber-500' },
    { key: 'pending', icon: AlertCircle, label: 'Ожидают', value: pendingAppointments.length, color: 'text-amber-400', actionable: true, to: '/appointments' },
    { key: 'week', icon: CalendarCheck, label: 'На этой неделе', value: thisWeekConfirmed, color: 'text-green-400' },
    { key: 'total', icon: Users, label: 'Всего подтв.', value: confirmedTotal, color: 'text-stone-400' },
  ];

  const activationSteps = [
    { icon: Settings, title: 'Настройте расписание', desc: 'Укажите рабочие часы и форматы встреч.', action: () => navigate('/settings') },
    { icon: Link2, title: 'Поделитесь ссылкой записи', desc: 'Отправьте клиентам ссылку или QR-код, и они запишутся сами.', action: () => setLinksOpen(true) },
    { icon: FileUp, title: 'Перенесите своих клиентов', desc: 'Загрузите таблицу или заметки, ИИ распознает базу за минуту.', action: () => navigate('/import') },
  ];

  return (
    <div data-testid="dashboard-page" className="flex min-h-screen" style={{ background: 'var(--app-bg)' }}>
      <Sidebar />
      <main className="flex-1 p-4 lg:p-8 pt-16 lg:pt-8 overflow-auto">
        <div className="max-w-6xl mx-auto">
          <div className="mb-10 animate-fade-in">
            <DayCycleGreeting name={settings?.psychologist_name} />
            <p className="text-stone-500 mt-3 text-center">
              {format(new Date(), "EEEE, d MMMM yyyy", { locale: ru })}
            </p>
          </div>

          {/* Owner: lightweight hub instead of an empty screen */}
          {isOwner && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-fade-in">
              <button onClick={() => navigate('/analytics')}
                className="text-left rounded-xl border p-6 transition-colors hover:-translate-y-0.5 duration-300"
                style={{ background: 'var(--app-card)', borderColor: 'var(--app-border)' }}>
                <BarChart3 size={24} style={{ color: 'var(--app-accent)' }} className="mb-3" />
                <h3 className="font-heading text-lg mb-1" style={{ color: 'var(--app-text)' }}>Аналитика</h3>
                <p className="text-sm" style={{ color: 'var(--app-text-secondary)' }}>Загрузка кабинетов, доходы и работа специалистов.</p>
              </button>
              <button onClick={() => navigate('/settings')}
                className="text-left rounded-xl border p-6 transition-colors hover:-translate-y-0.5 duration-300"
                style={{ background: 'var(--app-card)', borderColor: 'var(--app-border)' }}>
                <Settings size={24} style={{ color: 'var(--app-accent)' }} className="mb-3" />
                <h3 className="font-heading text-lg mb-1" style={{ color: 'var(--app-text)' }}>Настройки организации</h3>
                <p className="text-sm" style={{ color: 'var(--app-text-secondary)' }}>Команда, кабинеты, страница клиники и форматы.</p>
              </button>
            </div>
          )}

          {/* New / empty practice: activation checklist instead of four zeros */}
          {!isOwner && isEmpty && (
            <div className="rounded-2xl border p-6 sm:p-8 animate-fade-in" style={{ background: 'var(--app-card)', borderColor: 'var(--app-border)' }}>
              <h2 className="font-heading text-xl mb-1" style={{ color: 'var(--app-text)' }}>С чего начать</h2>
              <p className="text-sm mb-6" style={{ color: 'var(--app-text-muted)' }}>Три шага, и первая запись не заставит себя ждать.</p>
              <div className="space-y-3">
                {activationSteps.map((step, i) => (
                  <button key={i} onClick={step.action}
                    className="w-full flex items-center gap-4 text-left rounded-xl border p-4 transition-colors group"
                    style={{ background: 'var(--app-bg)', borderColor: 'var(--app-border)' }}>
                    <span className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 font-heading text-sm" style={{ background: 'var(--app-accent-muted)', color: 'var(--app-accent)' }}>{i + 1}</span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium" style={{ color: 'var(--app-text)' }}>{step.title}</span>
                      <span className="block text-xs mt-0.5" style={{ color: 'var(--app-text-secondary)' }}>{step.desc}</span>
                    </span>
                    <ArrowRight size={16} className="shrink-0 transition-transform group-hover:translate-x-0.5" style={{ color: 'var(--app-accent)' }} />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Active practice: stats + schedule */}
          {!isOwner && !isEmpty && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                {stats.map((stat, i) => {
                  const hot = stat.actionable && stat.value > 0;
                  const Comp = hot ? 'button' : 'div';
                  return (
                    <Comp key={stat.key} onClick={hot ? () => navigate(stat.to) : undefined}
                      data-testid={`stat-${stat.key}`}
                      className={`stat-card text-left rounded-xl border p-5 animate-fade-in delay-${i + 1} transition-colors ${hot ? 'cursor-pointer' : ''}`}
                      style={{ background: 'var(--app-card)', borderColor: hot ? 'var(--app-accent)' : 'var(--app-border)' }}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs uppercase tracking-wider" style={{ color: hot ? 'var(--app-accent)' : 'var(--app-text-muted)' }}>{stat.label}</p>
                          <p className="text-2xl font-semibold mt-1" style={{ color: 'var(--app-text)' }}>{stat.value}</p>
                        </div>
                        <stat.icon size={22} className={stat.color} />
                      </div>
                      {hot && (
                        <span className="text-[11px] mt-2 flex items-center gap-1" style={{ color: 'var(--app-accent)' }}>
                          Разобрать <ArrowRight size={11} />
                        </span>
                      )}
                    </Comp>
                  );
                })}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Today's Schedule */}
                <Card className="bg-stone-900 border-stone-800 animate-fade-in delay-2">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-stone-200 font-heading text-lg">Сегодня</CardTitle>
                      <Button data-testid="view-all-appointments" variant="ghost" size="sm"
                        onClick={() => navigate('/appointments')} className="text-stone-500 hover:text-amber-500">
                        Все записи <ArrowRight size={14} className="ml-1" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {todayAppointments.length === 0 ? (
                      <div className="py-6 text-center">
                        <Clock size={20} className="mx-auto mb-2" style={{ color: 'var(--app-text-faint)' }} />
                        <p className="text-sm" style={{ color: 'var(--app-text-muted)' }}>На сегодня записей нет. Свободный день.</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {todayAppointments.map((appt) => (
                          <div key={appt.id} data-testid={`today-appt-${appt.id}`}
                            className="flex items-center gap-4 p-3 rounded-lg bg-stone-950/40 border border-stone-800/50 hover:border-stone-700 transition-colors">
                            <div className="text-center min-w-[50px]">
                              <p className="text-amber-500 font-semibold text-sm">{appt.time}</p>
                              <p className="text-stone-600 text-xs">{appt.duration} мин</p>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-stone-300 text-sm font-medium truncate">{appt.patient_name}</p>
                              <p className="text-stone-600 text-xs">{appt.format_name}</p>
                            </div>
                            <Badge className={statusConfig[appt.status]?.className}>
                              {statusConfig[appt.status]?.label}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Pending Requests */}
                <Card className="bg-stone-900 border-stone-800 animate-fade-in delay-3">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-stone-200 font-heading text-lg">
                      Ожидают подтверждения
                      {pendingAppointments.length > 0 && (
                        <span className="ml-2 text-xs bg-amber-700/30 text-amber-400 px-2 py-0.5 rounded-full">
                          {pendingAppointments.length}
                        </span>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {pendingAppointments.length === 0 ? (
                      <div className="py-6 text-center">
                        <Check size={20} className="mx-auto mb-2" style={{ color: 'var(--app-text-faint)' }} />
                        <p className="text-sm" style={{ color: 'var(--app-text-muted)' }}>Всё разобрано. Новых заявок нет.</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {pendingAppointments.slice(0, 5).map((appt) => (
                          <div key={appt.id} data-testid={`pending-appt-${appt.id}`}
                            className="flex items-center gap-4 p-3 rounded-lg bg-stone-950/40 border border-amber-900/20 hover:border-amber-800/40 transition-colors">
                            <div className="text-center min-w-[70px]">
                              <p className="text-stone-400 text-xs">
                                {(() => {
                                  try {
                                    const d = parseISO(appt.date);
                                    if (isToday(d)) return 'Сегодня';
                                    if (isTomorrow(d)) return 'Завтра';
                                    return format(d, 'd MMM', { locale: ru });
                                  } catch { return appt.date; }
                                })()}
                              </p>
                              <p className="text-amber-500 font-semibold text-sm">{appt.time}</p>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-stone-300 text-sm font-medium truncate">{appt.patient_name}</p>
                              <p className="text-stone-600 text-xs">{appt.format_name} / {appt.patient_phone}</p>
                            </div>
                            <Badge className={statusConfig[appt.status]?.className + ' text-[10px] mr-1'}>
                              {statusConfig[appt.status]?.label}
                            </Badge>
                            <div className="flex items-center gap-1">
                              <button data-testid={`dash-confirm-${appt.id}`} onClick={() => confirmAppt(appt.id)}
                                aria-label={`Подтвердить запись: ${appt.patient_name}`}
                                className="w-9 h-9 flex items-center justify-center rounded-md text-green-500 hover:bg-green-900/20 transition-colors"
                                title={appt.status === 'pending_admin' ? 'Одобрить (→ врачу)' : 'Подтвердить'}>
                                <Check size={16} />
                              </button>
                              <button data-testid={`dash-cancel-${appt.id}`} onClick={() => setConfirmCancel(appt)}
                                aria-label={`Отклонить запись: ${appt.patient_name}`}
                                className="w-9 h-9 flex items-center justify-center rounded-md text-red-500 hover:bg-red-900/20 transition-colors"
                                title="Отклонить">
                                <XCircle size={16} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </div>
      </main>

      <BookingLinksDialog open={linksOpen} onOpenChange={setLinksOpen} />

      <AlertDialog open={!!confirmCancel} onOpenChange={(o) => !o && setConfirmCancel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Отменить запись?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmCancel?.patient_name ? `Запись клиента «${confirmCancel.patient_name}» будет отменена. ` : ''}
              Клиент получит уведомление об отмене.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Назад</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { const a = confirmCancel; setConfirmCancel(null); if (a) cancelAppt(a.id); }}
              className="bg-red-600 hover:bg-red-700 text-white">
              Отменить запись
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
